import {
  type AdaptedWallet,
  MAINNET_RELAY_API,
  type ProgressData,
} from '@relayprotocol/relay-sdk'
import type { Address, Hex, WalletClient } from 'viem'
import type { BitcoinAddress, BitcoinTxHash, BitcoinWallet } from './bitcoin'
import { getRelayClient } from './client'
import type {
  RELAY_BITCOIN_CHAIN_ID,
  RELAY_LIGHTER_CHAIN_ID,
  RELAY_SOLANA_CHAIN_ID,
  RELAY_TRON_CHAIN_ID,
} from './constants'
import type { LighterAccountIndex, LighterWallet } from './lighter'
import type { RelayQuoteResult } from './quote'
import type { SolanaAddress, SolanaTxHash, SolanaWallet } from './solana'
import type { TronAddress, TronTxHash, TronWallet } from './tron'
import type { Logger, RelayExecutionInfo } from './types'

export type RelayExecutionStep = ProgressData['currentStep'] & {}

export const RELAY_VM_TYPES = [
  'bvm',
  'evm',
  'hypevm',
  'svm',
  'lvm',
  'tvm',
] as const

export type RelayVmType = (typeof RELAY_VM_TYPES)[number]

export type RelayAddress<VmType extends RelayVmType = RelayVmType> = {
  bvm: BitcoinAddress
  evm: Address
  hypevm: Address
  svm: SolanaAddress
  lvm: LighterAccountIndex
  tvm: TronAddress
}[VmType]

export type RelayChainId<VmType extends RelayVmType = RelayVmType> = {
  bvm: typeof RELAY_BITCOIN_CHAIN_ID
  evm: string
  hypevm: string
  svm: typeof RELAY_SOLANA_CHAIN_ID
  lvm: typeof RELAY_LIGHTER_CHAIN_ID
  tvm: typeof RELAY_TRON_CHAIN_ID
}[VmType]

export type RelayTxHash<VmType extends RelayVmType = RelayVmType> = {
  bvm: BitcoinTxHash
  evm: Hex
  hypevm: Hex
  svm: SolanaTxHash
  lvm: Hex
  tvm: TronTxHash
}[VmType]

export type RelayWallet<VmType extends RelayVmType = RelayVmType> = {
  bvm: BitcoinWallet
  evm: WalletClient | (AdaptedWallet & { vmType: 'evm' })
  hypevm: AdaptedWallet & { vmType: 'hypevm' }
  svm: SolanaWallet
  lvm: LighterWallet
  tvm: TronWallet
}[VmType]

export interface ExecuteRelayQuoteParams<VmType extends RelayVmType> {
  logger: Logger
  onError: (error: Error) => Promise<void>
  onProgress?: (step: RelayExecutionStep | null) => void
  /**
   * Fired when all *input* transactions are confirmed on-chain.
   */
  onTransactionConfirmed?: (txHash: RelayTxHash<VmType>) => Promise<void>
  /**
   * Fired as soon as user has *signed* all input transactions, but before they are confirmed on-chain.
   *
   * After this event, no more action should be needed from the user.
   * However, it is possible that the transactions will be rejected and the execution will NOT continue.
   */
  onUserActionsCompleted?: (txHash: RelayTxHash<VmType>) => Promise<void>
  relayQuote: RelayQuoteResult<VmType>
  walletClient: RelayWallet<VmType>

  /**
   * Type of transaction being executed
   *
   * - `'standard'` - regular transaction (default)
   * - `'erc4337'` - ERC-4337 smart wallet transaction
   */
  transactionType?: 'standard' | 'erc4337'
}

interface DefaultTxHashParams {
  transactionType: 'standard' | 'erc4337'
  logger: Logger

  // This typing is not correct but that's what Relay returns
  // Read Loic's PR #2565 for more details
  txHashes: {
    txHash: string
  }[]
}

/**
 * Default mode. Supported for all VmTypes.
 * Uses `txHashes[0].txHash` property from onProgress callback to
 * extract the txHash.
 *
 * Used as a fallback for receipt mode
 */
interface TxHashesGetTxHashParams extends DefaultTxHashParams {
  transactionType: 'standard'
  steps?: unknown[] // steps is not used in this mode
}

/**
 * This is solely for ERC-4337 smart wallet transactions
 *
 * Uses `steps[0].items[0].receipt.transactionHash` property from onProgress callback
 * to extract the txHash
 *
 * Only used in smart wallet (account abstraction, ERC-4337) transactions
 */
interface ReceiptGetTxHashParams extends DefaultTxHashParams {
  transactionType: 'erc4337'
  steps: {
    items: {
      receipt?: RelayExecutionStep['items'][number]['receipt']
    }[]
  }[]
}

type GetTxHashParams = TxHashesGetTxHashParams | ReceiptGetTxHashParams

function getTxHash<VmType extends RelayVmType>({
  logger,
  transactionType,
  txHashes,
  steps,
}: GetTxHashParams): RelayTxHash<VmType> {
  const logPrefix = 'getTxHash'
  const logProps = {
    logger,
    transactionType,
    txHashes,
    steps,
  }
  logger.info(`${logPrefix}:started`, logProps)

  const getTxHashFromTxHashes = () => {
    // From PR #2565 by Loïc Nicolas
    // Use first txHash as the main txHash in fun DE table
    // Bad surprise from Relay: txHash may be an object, this is not present in their typing...
    const rawTxHashWithUnreliableTyping = txHashes[0]?.txHash as
      | RelayTxHash<VmType>
      | { id: RelayTxHash<VmType> }

    // rawTxHashWithUnreliableTyping cannot be null so typeof check is enough
    const txHash =
      typeof rawTxHashWithUnreliableTyping === 'object'
        ? rawTxHashWithUnreliableTyping.id
        : rawTxHashWithUnreliableTyping
    logger.info(`${logPrefix}:txHashesMode`, {
      ...logProps,
      txHash,
    })
    if (!txHash) {
      logger.error(`${logPrefix}:txHashesMode:noTxHash`, {
        ...logProps,
        txHash,
      })
    }
    return txHash
  }

  const getTxHashFromReceipt = () => {
    if (transactionType !== 'erc4337') {
      throw new Error(
        `Transaction type ${transactionType} is not supported for receipt mode`,
      )
    }

    const receipt = steps?.[0]?.items?.[0]?.receipt

    // This one excludes SVM and Sui receipts (which are not supported for this mode)
    const txHash =
      receipt && 'transactionHash' in receipt
        ? receipt.transactionHash
        : undefined
    logger.info(`${logPrefix}:receiptMode`, {
      ...logProps,
      txHash,
    })
    return txHash as RelayTxHash<VmType>
  }

  switch (transactionType) {
    // txHashes mode is the default mode
    case 'standard': {
      const txHash = getTxHashFromTxHashes()
      return txHash
    }
    // receipt mode is used for ERC-4337 smart wallet transactions
    // unsupported for Solana Virtual Machine (SVM) or Sui (sui) transactions
    case 'erc4337': {
      const txHash = getTxHashFromReceipt()
      if (!txHash) {
        logger.info(`${logPrefix}:receiptMode:fallbackToTxHashes`, {
          ...logProps,
          txHash,
        })
        return getTxHashFromTxHashes()
      }

      return txHash
    }
    default: {
      throw new Error(`Unknown transactionType: ${transactionType}`)
    }
  }
}

export async function executeRelayQuote<VmType extends RelayVmType>({
  logger,
  onError,
  onProgress,
  onTransactionConfirmed,
  onUserActionsCompleted,
  relayQuote,
  walletClient,
  transactionType = 'standard',
}: ExecuteRelayQuoteParams<VmType>): Promise<void> {
  let isUserActionsCompleted = false
  let isTransactionConfirmed = false

  await getRelayClient().actions.execute({
    quote: relayQuote,
    wallet: walletClient,
    onProgress: async ({
      steps,
      fees,
      breakdown,
      currentStep,
      currentStepItem,
      txHashes,
      details,
      error,
    }) => {
      const logPrefix = 'onRelayProgress'
      logger.info(`${logPrefix}:params`, {
        steps,
        currentStep,
        fees,
        breakdown,
        currentStepItem,
        txHashes,
        details,
        error,
      })

      onProgress?.(currentStep ?? null)

      if (error) {
        logger.info(`${logPrefix}:errorDetected`, error)
        await onError(error).catch((e) => {
          logger.error(`${logPrefix}:onErrorFailed`, e)
        })
      } else if (txHashes?.length) {
        const txHash = getTxHash<VmType>({
          transactionType,
          logger,
          txHashes,
          steps,
        })

        if (
          // Call onUserActionsCompleted only once
          !isUserActionsCompleted &&
          steps.every((step) =>
            step.items.every(
              // 'completed' - catch-all for items marked completed with no other info
              //    - e.g. Batched items will not have txHash but will have their item marked 'complete'
              // 'pending' - for origin chain transactions that have been included on chain
              //    - may sometimes be skipped, hence the need for 'submitted' condition below
              // 'submitted' - for destination chain transactions that have been submitted to the network
              // receipt - is present when the transaction has been mined/confirmed
              // Ensure onUserActionsCompleted is always called before onTransactionConfirmed
              (item) =>
                item.status === 'complete' ||
                item.checkStatus === 'pending' ||
                item.checkStatus === 'submitted' ||
                item.receipt !== undefined,
            ),
          )
        ) {
          logger.info(`${logPrefix}:onUserActionsCompleted`, txHashes)
          isUserActionsCompleted = true

          try {
            await onUserActionsCompleted?.(txHash)
          } catch (e) {
            logger.error(
              `${logPrefix}:onUserActionsCompletedFailed`,
              e as Error,
            )
          }
        }

        if (
          // Call onTransactionConfirmed only once
          !isTransactionConfirmed &&
          steps.every((step) =>
            step.items.every((item) => item.status === 'complete'),
          )
        ) {
          logger.info(`${logPrefix}:onTransactionConfirmed`, txHashes)
          isTransactionConfirmed = true

          try {
            await onTransactionConfirmed?.(txHash)
          } catch (e) {
            logger.error(
              `${logPrefix}:onTransactionConfirmedFailed`,
              e as Error,
            )
          }
        }
      }
    },
  })
}

export async function getRelayExecutionInfo(
  requestId: string,
  apiKey: string,
): Promise<RelayExecutionInfo> {
  const url = `${MAINNET_RELAY_API}/intents/status/v2?requestId=${requestId}`
  const response = await fetch(url, {
    headers: { 'x-api-key': apiKey },
  })
  if (!response.ok) {
    throw Error(response.statusText)
  }

  const data = await response.json()
  return data as RelayExecutionInfo
}
